import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { TimeMapping } from "../api/types";
import { streamToSession, sessionToStream, streamSegment } from "./mapping";
import { NS, clamp, nearestIndex } from "./time";
import { alignPausedMedia } from "./pausedMedia";
import { preparePlayback } from "./preparePlayback";

/**
 * One master clock for every media element on screen.
 *
 * The unit is **session nanoseconds** — the same domain the backend, the frame
 * index, the timeline and the annotations use. Each element converts into it
 * through two numbers:
 *
 *   sessionNs = mapping((element.currentTime × 1e9 − elementStartNs) + playbackOffsetNs)
 *
 * `playbackOffsetNs` is the stream's first PTS, which the proxy transcode
 * deliberately does not carry inside the file (see media/proxy.py).
 * `elementStartNs` is what the browser reports as the start of the media —
 * zero for our rebased proxies, non-zero for an original whose edit list the
 * browser chose to honour. Reading it instead of assuming means the same code
 * is correct for both, and correct in browsers that disagree with each other.
 *
 * The left eye is the clock; the right eye and audio are followers, corrected
 * by trimming their playback rate and hard-seeking only when they are far out.
 * Nudging currentTime every frame would stutter; letting them free-run would
 * drift.
 */

export interface MediaSource {
  key: string;
  url: string;
  playbackOffsetNs: number;
  mapping?: TimeMapping;
  kind: "video" | "audio";
  stillPreview?: boolean; // Paused depth comes from its exact stored frame.
}

export interface SyncPlayerOptions {
  sources: MediaSource[];
  clockKey: string;
  frameTimes?: Record<string, number[]>; // key -> session ns per frame
  rangeNs: [number, number];
}

export interface FollowerState {
  key: string;
  driftMs: number;
  ready: boolean;
  corrected: boolean;
}

const HARD_SEEK_S = 0.08;
const TRIM_THRESHOLD_S = 0.012;
const MAX_TRIM = 0.06;

export function useSyncPlayer(opts: SyncPlayerOptions) {
  const { sources, clockKey, rangeNs } = opts;
  const elements = useRef<Record<string, HTMLMediaElement | null>>({});
  const elementStart = useRef<Record<string, number>>({});
  const [ready, setReady] = useState<Record<string, boolean>>({});
  const [playing, setPlaying] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [seekRevision, setSeekRevision] = useState(0);
  const startedRef = useRef(false);
  const [rate, setRateState] = useState(1);
  const [masterNs, setMasterNs] = useState(rangeNs[0]);
  const [followers, setFollowers] = useState<FollowerState[]>([]);
  const [diagnostics, setDiagnostics] = useState<{fps: number; dropped: number; total: number} | null>(null);
  const [blockedReason, setBlockedReason] = useState<string | null>(null);
  const [loop, setLoop] = useState<[number, number] | null>(null);
  const masterRef = useRef(masterNs);
  const loopRef = useRef(loop);
  const rateRef = useRef(rate);
  const playingRef = useRef(playing);
  playingRef.current = playing;
  loopRef.current = loop;
  rateRef.current = rate;

  const byKey = useMemo(() => {
    const map: Record<string, MediaSource> = {};
    for (const s of sources) map[s.key] = s;
    return map;
  }, [sources]);

  /* One stable callback per key.
   *
   * Returning a fresh arrow from `register(key)` made React detach and
   * re-attach every media element on every render — and the master clock
   * updates state every animation frame, so that was constant. Anything
   * reading `elements.current` between the detach and the re-attach saw null,
   * which is how a play or pause could reach some elements and miss others,
   * leaving the audio running against a stopped clock. */
  const registrars = useRef<Record<string, (el: HTMLMediaElement | null) => void>>({});
  const register = useCallback((key: string) => {
    if (!registrars.current[key]) {
      registrars.current[key] = (el: HTMLMediaElement | null) => {
        elements.current[key] = el;
      };
    }
    return registrars.current[key];
  }, []);
  const getElement = useCallback((key: string) => elements.current[key] ?? null, []);

  const toSession = useCallback(
    (key: string, elementSeconds: number) => {
      const src = byKey[key];
      if (!src) return 0;
      return streamToSession(src.mapping, elementSeconds * NS - (elementStart.current[key] ?? 0) + src.playbackOffsetNs);
    },
    [byKey],
  );

  const toElement = useCallback(
    (key: string, sessionNs: number) => {
      const src = byKey[key];
      if (!src) return 0;
      return (sessionToStream(src.mapping, sessionNs) - src.playbackOffsetNs + (elementStart.current[key] ?? 0)) / NS;
    },
    [byKey],
  );

  const onLoadedMetadata = useCallback((key: string) => {
    const el = elements.current[key];
    if (!el) return;
    // What the browser thinks is the start of this media. Proxies are rebased
    // to zero; an original may or may not carry its edit list through.
    elementStart.current[key] = el.seekable.length > 0 ? Math.round(el.seekable.start(0) * NS) : 0;
    const target = toElement(key, masterRef.current);
    if (Number.isFinite(target) && (!byKey[key]?.stillPreview || playingRef.current)) el.currentTime = clamp(target, 0, Math.max(0, el.duration - 0.001));
    setReady((prev) => ({ ...prev, [key]: true }));
  }, [toElement, byKey]);

  /** Move every element to one instant. */
  const seek = useCallback(
    (sessionNs: number, opts: { pause?: boolean } = {}) => {
      const clamped = clamp(sessionNs, rangeNs[0], rangeNs[1]);
      masterRef.current = clamped;
      setMasterNs(clamped);
      if (playingRef.current && !opts.pause) {
        startedRef.current = false; setPreparing(true); setSeekRevision(value => value + 1);
      }
      for (const src of sources) {
        const el = elements.current[src.key];
        if (!el) continue;
        if (src.stillPreview && (!playingRef.current || opts.pause)) continue;
        const target = toElement(src.key, clamped);
        const duration = Number.isFinite(el.duration) ? el.duration : Infinity;
        // Outside this element's own span (the eyes start up to ~140 ms apart,
        // and audio can begin seconds before video) it simply parks at the edge.
        el.currentTime = clamp(target, 0, Math.max(0, duration - 0.001));
      }
      if (opts.pause) {
        setPlaying(false);
      }
    },
    [rangeNs, sources, toElement],
  );

  // A sync edit must also reposition paused media, whose rAF correction is stopped.
  useEffect(() => { seek(masterRef.current); }, [seek]);

  useEffect(() => {
    if (playing) return;
    const media = sources.map(src => ({src, el: elements.current[src.key]}));
    const align = () => {
      for (const {src, el} of media) {
        if (!el) continue;
        if (src.stillPreview) el.pause();
        else alignPausedMedia(el, toElement(src.key, masterRef.current));
      }
    };
    align();
    for (const {el} of media) for (const event of ["seeked", "loadeddata", "canplay"]) el?.addEventListener(event, align);
    return () => { for (const {el} of media) for (const event of ["seeked", "loadeddata", "canplay"]) el?.removeEventListener(event, align); };
  }, [playing, sources, toElement, ready]);

  // Give frame previews priority, then quietly prepare videos after scrubbing settles.
  useEffect(() => {
    if (playing) return;
    const timer = window.setTimeout(() => {
      for (const src of sources) {
        if (!src.stillPreview) continue;
        const el = elements.current[src.key];
        if (!el || el.readyState < 1) continue;
        const target = toElement(src.key, masterRef.current);
        const end = Number.isFinite(el.duration) ? Math.max(0, el.duration - 0.001) : Infinity;
        const want = Math.min(end, Math.max(0, target));
        if (Math.abs(el.currentTime - want) > 0.005) el.currentTime = want;
      }
    }, 600);
    return () => window.clearTimeout(timer);
  }, [playing, masterNs, sources, toElement, ready]);

  const play = useCallback(() => {
    if (playingRef.current) return;
    setBlockedReason(null);
    setPreparing(true);
    setPlaying(true);
  }, []);
  const pause = useCallback(() => setPlaying(false), []);
  const toggle = useCallback(() => {
    setBlockedReason(null);
    if (playingRef.current) setPlaying(false);
    else { setPreparing(true); setPlaying(true); }
  }, []);

  const setRate = useCallback((value: number) => setRateState(value), []);

  /* Play/pause: the clock element leads, the rest follow it in the rAF loop. */
  useEffect(() => {
    const clock = elements.current[clockKey];
    if (!clock) return;

    // Pause everything ever registered, not just what is in `sources` now: a
    // layer just switched off must not be left running.
    const pauseAll = () => {
      for (const element of Object.values(elements.current)) element?.pause();
    };

    if (!playing) {
      startedRef.current = false;
      setPreparing(false);
      pauseAll();
      return;
    }
    startedRef.current = false;
    setPreparing(true);
    let cancelled = false;
    const controller = new AbortController();
    void (async () => {
      try {
        const anchor = masterRef.current;
        await Promise.all(sources.filter(src => src.kind === "video").map(src => {
          const el = elements.current[src.key];
          return el ? preparePlayback(el, toElement(src.key, anchor), controller.signal) : Promise.resolve();
        }));
        if (cancelled) return;
        const starts: Promise<void>[] = [clock.play()];
        for (const src of sources) {
          if (src.key === clockKey) continue;
          const follower = elements.current[src.key];
          if (follower) {
            const target = toElement(src.key, anchor);
            if (Math.abs(target - follower.currentTime) > HARD_SEEK_S) follower.currentTime = Math.max(0, target);
            if (src.kind === "video") starts.push(follower.play());
            else void follower.play().catch(() => undefined);
          }
        }
        await Promise.all(starts);
        if (cancelled) { pauseAll(); return; }
        startedRef.current = true;
        setPreparing(false);
      } catch (error: any) {
        if (cancelled) return;
        pauseAll();
        setPreparing(false);
        setPlaying(false);
        setBlockedReason(String(error?.message ?? error));
      }
    })();
    return () => { cancelled = true; startedRef.current = false; controller.abort(); pauseAll(); };
  }, [playing, clockKey, sources, toElement, toSession, seekRevision]);

  useEffect(() => {
    for (const src of sources) {
      const el = elements.current[src.key];
      if (el && src.key === clockKey) el.playbackRate = rate / streamSegment(src.mapping, sessionToStream(src.mapping, masterRef.current)).a;
    }
  }, [rate, sources, clockKey]);

  // Count frames submitted to the compositor. Decoder counters can include
  // frames decoded during seeks, which overstates the visible playback rate.
  useEffect(() => {
    if (!playing) return;
    const video = elements.current[clockKey];
    if (!(video instanceof HTMLVideoElement) || !video.requestVideoFrameCallback) return;
    setDiagnostics(null);
    let callback = 0;
    let measuredAt: number | null = null;
    let lastFrames = 0;
    const measure = (now: number, metadata: VideoFrameCallbackMetadata) => {
      if (measuredAt === null) { measuredAt = now; lastFrames = metadata.presentedFrames; }
      if (now - measuredAt >= 1000) {
        const quality = video.getVideoPlaybackQuality();
        setDiagnostics({fps:(metadata.presentedFrames-lastFrames)*1000/(now-measuredAt),
          dropped:quality.droppedVideoFrames,total:quality.totalVideoFrames});
        measuredAt = now; lastFrames = metadata.presentedFrames;
      }
      callback = video.requestVideoFrameCallback(measure);
    };
    callback = video.requestVideoFrameCallback(measure);
    return () => video.cancelVideoFrameCallback(callback);
  }, [playing, clockKey, sources]);

  /* The loop: read the clock, publish master time, correct the followers. */
  useEffect(() => {
    if (!playing) return;
    let raf = 0;
    let publishedAt = -Infinity;
    const tick = (now: number) => {
      if (!startedRef.current) { raf = requestAnimationFrame(tick); return; }
      if (now - publishedAt < 1000 / 30) { raf = requestAnimationFrame(tick); return; }
      publishedAt = now;
      const clock = elements.current[clockKey];
      if (clock) {
        if (clock.ended && !loopRef.current) { setPlaying(false); return; }
        if (clock.seeking || clock.readyState < 2) { raf = requestAnimationFrame(tick); return; }
        let ns = toSession(clockKey, clock.currentTime);
        const range = loopRef.current;
        if (range && ns >= range[1]) {
          ns = range[0];
          const target = toElement(clockKey, ns);
          clock.currentTime = Math.max(0, target);
          for (const src of sources) {
            if (src.key === clockKey) continue;
            const el = elements.current[src.key];
            if (el) el.currentTime = Math.max(0, toElement(src.key, ns));
          }
        }
        const clockSource = byKey[clockKey];
        clock.playbackRate = rateRef.current / streamSegment(clockSource?.mapping, sessionToStream(clockSource?.mapping, ns)).a;
        masterRef.current = ns;
        setMasterNs(ns);

        const next: FollowerState[] = [];
        for (const src of sources) {
          if (src.key === clockKey) continue;
          const el = elements.current[src.key];
          if (!el) continue;
          const want = toElement(src.key, ns);
          const diff = want - el.currentTime;
          const baseRate = rateRef.current / streamSegment(src.mapping, sessionToStream(src.mapping, ns)).a;
          let corrected = false;
          if (want < 0 || (Number.isFinite(el.duration) && want > el.duration)) {
            // Outside this stream's span: leave it alone rather than seeking
            // to a clamped position and reporting a drift that is not real.
            el.playbackRate = baseRate;
          } else if (!el.seeking && Math.abs(diff) > HARD_SEEK_S) {
            el.currentTime = want;
            corrected = true;
          } else if (!el.seeking && Math.abs(diff) > TRIM_THRESHOLD_S) {
            el.playbackRate = baseRate * (1 + clamp(diff * 2, -MAX_TRIM, MAX_TRIM));
            corrected = true;
          } else {
            el.playbackRate = baseRate;
          }
          if (el.paused && !el.ended && el.readyState >= 2 && want >= 0) void el.play().catch(() => undefined);
          next.push({ key: src.key, driftMs: diff * 1000, ready: !!ready[src.key], corrected });
        }
        setFollowers(next);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing, clockKey, sources, toSession, toElement, ready]);

  /** Step whole frames on a given stream's real frame timestamps.
   *
   * Without a frame index there is nothing to step *to*. Guessing an interval
   * from a nominal frame rate would land between frames on this VFR footage —
   * so the step is refused and the caller disables the control instead. */
  const canStep = (opts.frameTimes?.[clockKey]?.length ?? 0) > 0;
  const stepFrames = useCallback(
    (delta: number, key = clockKey) => {
      const times = opts.frameTimes?.[key];
      if (!times || times.length === 0) return;
      const current = nearestIndex(times, masterRef.current);
      const next = clamp(current + delta, 0, times.length - 1);
      setPlaying(false);
      seek(times[next], {pause: true});
    },
    [clockKey, opts.frameTimes, seek],
  );

  const frameAt = useCallback(
    (key: string, ns = masterNs) => {
      const times = opts.frameTimes?.[key];
      if (!times || times.length === 0) return null;
      const i = nearestIndex(times, ns);
      return { index: i, ns: times[i], deltaNs: times[i] - ns };
    },
    [opts.frameTimes, masterNs],
  );

  return {
    diagnostics, masterNs, setMasterNs, playing, preparing, rate, setRate, play, pause, toggle, seek,
    stepFrames, canStep, frameAt, register, getElement, onLoadedMetadata, ready, followers,
    blockedReason, loop, setLoop, toElement, toSession,
  };
}
